import { IPlatformSetting } from '@core/models/integration.model';
import React, { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import integrationsService from 'services/api/integrations.service';
import { integrationErrorMessage } from './integration-user-messages';

const SETTING_LABELS: Record<string, string> = {
    'as2.local_id_test': 'Antar AS2 ID (test)',
    'as2.local_id_prod': 'Antar AS2 ID (production)',
    'as2.inbound_url_test': 'Inbound URL (test)',
    'as2.inbound_url_prod': 'Inbound URL (production)',
    'as2.enabled': 'AS2 integration enabled',
    'alerts.email_to': 'Internal copy for all integration emails (comma-separated)',
    'notifications.enabled': 'Send partner lifecycle emails (partner created, MDN, failures, cert expiry)',
};

const IntegrationPlatformSettingsPanel: FC = () => {
    const [settings, setSettings] = useState<IPlatformSetting[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r: any = await integrationsService.getPlatformSettings();
            setSettings(r.data.platformSettings || []);
        } catch (err) {
            toast.error(integrationErrorMessage(err, 'Could not load platform settings.'), { autoClose: 5000 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const setValue = (key: string, value: string) => {
        setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, value } : s)));
    };

    const save = async () => {
        setSaving(true);
        try {
            await integrationsService.putPlatformSettings(settings);
            toast.success('Platform settings saved', { autoClose: 5000 });
            load();
        } catch (err) {
            toast.error(integrationErrorMessage(err, 'Could not save platform settings.'), { autoClose: 5000 });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <p className="text-sm text-slate-500">Loading platform settings…</p>;
    }

    return (
        <div className="rounded-md border border-slate-200 bg-white p-4">
            <h4 className="mb-3 text-sm font-semibold text-slate-800">Platform settings</h4>
            <div className="space-y-3">
                {settings.map((s) => (
                    <div key={s.key}>
                        <label className="mb-1 block text-xs font-medium text-slate-700">
                            {SETTING_LABELS[s.key] || s.key}
                        </label>
                        {s.key === 'as2.enabled' || s.key === 'notifications.enabled' ? (
                            <select
                                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                                value={s.value || 'false'}
                                onChange={(e) => setValue(s.key, e.target.value)}
                            >
                                <option value="true">Enabled</option>
                                <option value="false">Disabled</option>
                            </select>
                        ) : (
                            <input
                                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                                value={s.value || ''}
                                onChange={(e) => setValue(s.key, e.target.value)}
                            />
                        )}
                    </div>
                ))}
            </div>
            <button
                type="button"
                onClick={save}
                disabled={saving}
                className="mt-4 rounded-md bg-[#bd902e] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#a07825] disabled:opacity-50"
            >
                {saving ? 'Saving…' : 'Save settings'}
            </button>
        </div>
    );
};

export default IntegrationPlatformSettingsPanel;
